import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./LeanVSM1.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function LeanVSM1(props) {
  const { x1200PxLogo_Icam__20081 } = props;
  const [hasImplemented, setHasImplemented] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    // Part 1: Understand the current state
    processSteps: "",
    cycleTimes: "",
    leadTimes: "",
    valueAddedPercentage: "",
    processTypes: "",
    physicalFlows: "",
    stockTypes: "",
    orderTransmission: "",
    informationSystems: "",
    dataUpdateFrequency: "",
    identifiedWaste: "",
    errorSteps: "",
    defectRates: "",

    // Part 2: Future vision
    futureObjectives: "",
    kpiToImprove: "",
    targetCycleTime: "",
    stepsToEliminate: "",
    wasteToEliminate: "",
    leanTools: "",
    plannedChanges: "",
    plannedTraining: "",
    transformationConstraints: "",

    // Part 3: Comparison and roadmap
    measuredGaps: "",
    trackedKPIs: "",
    roadmap: ""
  });

  const [uploadStatus, setUploadStatus] = useState({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
    fileName: ""
  });
  const fileInputRef = useRef(null);
  const questionnaireRef = useRef(null);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // File type check
    const allowedTypes = [
      'application/pdf', 'image/jpeg', 'image/png', 'image/svg+xml',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];

    if (!allowedTypes.includes(file.type)) {
      setUploadStatus({
        ...uploadStatus,
        error: "Unsupported file type. Please use PDF, JPG, PNG, SVG, Excel or Word.",
        success: false
      });
      return;
    }

    // Max size
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadStatus({
        ...uploadStatus,
        error: "File too large. Maximum size: 10MB.",
        success: false
      });
      return;
    }

    try {
      setUploadStatus({
        isUploading: true,
        progress: 0,
        error: null,
        success: false,
        fileName: file.name
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('answers', JSON.stringify(answers));

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadStatus(prev => ({
            ...prev,
            progress
          }));
        }
      };

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status === 200) resolve(JSON.parse(xhr.response));
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
      });

      xhr.open('POST', '/api/vsm/upload');
      xhr.send(formData);

      const response = await uploadPromise;

      setUploadStatus({
        isUploading: false,
        progress: 100,
        error: null,
        success: true,
        fileName: file.name,
        fileId: response.file_id
      });
    } catch (error) {
      setUploadStatus({
        isUploading: false,
        progress: 0,
        error: "An error occurred during upload. Please try again.",
        success: false,
        fileName: ""
      });
    }
  };

  const handleAnswerChange = (field, value) => {
    setAnswers({
      ...answers,
      [field]: value
    });
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      if (questionnaireRef.current) questionnaireRef.current.scrollTop = 0;
    } else {
      setShowImport(true);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (questionnaireRef.current) questionnaireRef.current.scrollTop = 0;
    }
  };

  const handleYesClick = () => {
    setHasImplemented(true);
    setShowQuestionnaire(true);
  };

  // Progress indicator
  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>1</div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>2</div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
      </div>
    );
  };

  // Questionnaire rendering
  const renderQuestionnaire = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 1: Understanding the current value stream</h2>

            <div className="question-group">
              <h3 className="question-group-title">Processes & physical flows:</h3>

              <div className="question-item">
                <label htmlFor="processSteps">
                  What are the main steps in your process, from customer order to delivery?
                </label>
                <textarea id="processSteps" value={answers.processSteps}
                  onChange={(e) => handleAnswerChange('processSteps', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="cycleTimes">
                  What are the cycle times for each key step?
                </label>
                <textarea id="cycleTimes" value={answers.cycleTimes}
                  onChange={(e) => handleAnswerChange('cycleTimes', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="leadTimes">
                  What are the waiting times (lead time) between each step?
                </label>
                <textarea id="leadTimes" value={answers.leadTimes}
                  onChange={(e) => handleAnswerChange('leadTimes', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="valueAddedPercentage">
                  Have you measured the percentage of value-added time vs total process time?
                </label>
                <textarea id="valueAddedPercentage" value={answers.valueAddedPercentage}
                  onChange={(e) => handleAnswerChange('valueAddedPercentage', e.target.value)} rows="2" />
              </div>

              <div className="question-item">
                <label htmlFor="processTypes">
                  Which steps are manual, automated, or hybrid?
                </label>
                <textarea id="processTypes" value={answers.processTypes}
                  onChange={(e) => handleAnswerChange('processTypes', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="physicalFlows">
                  What are the current physical flows from raw materials to finished products?
                </label>
                <textarea id="physicalFlows" value={answers.physicalFlows}
                  onChange={(e) => handleAnswerChange('physicalFlows', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="stockTypes">
                  What types of stock (WIP, safety, buffer) are held at each step?
                </label>
                <textarea id="stockTypes" value={answers.stockTypes}
                  onChange={(e) => handleAnswerChange('stockTypes', e.target.value)} rows="3" />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">Information flows:</h3>

              <div className="question-item">
                <label htmlFor="orderTransmission">
                  How are customer orders transmitted through the system?
                </label>
                <textarea id="orderTransmission" value={answers.orderTransmission}
                  onChange={(e) => handleAnswerChange('orderTransmission', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="informationSystems">
                  What tools or information systems do you use to manage information flows?
                </label>
                <textarea id="informationSystems" value={answers.informationSystems}
                  onChange={(e) => handleAnswerChange('informationSystems', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="dataUpdateFrequency">
                  How frequently are supply chain data updated in your system?
                </label>
                <textarea id="dataUpdateFrequency" value={answers.dataUpdateFrequency}
                  onChange={(e) => handleAnswerChange('dataUpdateFrequency', e.target.value)} rows="2" />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">âš ï¸ Waste (Muda):</h3>

              <div className="question-item">
                <label htmlFor="identifiedWaste">
                  What forms of waste have you identified (overproduction, waiting, unnecessary movement, etc.)?
                </label>
                <textarea id="identifiedWaste" value={answers.identifiedWaste}
                  onChange={(e) => handleAnswerChange('identifiedWaste', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="errorSteps">
                  At which steps are errors or rework most common?
                </label>
                <textarea id="errorSteps" value={answers.errorSteps}
                  onChange={(e) => handleAnswerChange('errorSteps', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="defectRates">
                  Have you measured defect or non-conformity rates in the process?
                </label>
                <textarea id="defectRates" value={answers.defectRates}
                  onChange={(e) => handleAnswerChange('defectRates', e.target.value)} rows="2" />
              </div>
            </div>
          </div>
        );

      /* ----------------- PART 2 (translated) ----------------- */

      case 2:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 2: Future vision / desired state</h2>

            <div className="question-group">
              <h3 className="question-group-title">Objectives & strategy:</h3>

              <div className="question-item">
                <label htmlFor="futureObjectives">
                  What are the main objectives of your desired future state (lead time reduction, quality improvement, stock reduction, etc.)?
                </label>
                <textarea id="futureObjectives" value={answers.futureObjectives}
                  onChange={(e) => handleAnswerChange('futureObjectives', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="kpiToImprove">
                  Which KPIs do you intend to improve (OTD, Takt Time, logistics cost, service rateâ€¦)?
                </label>
                <textarea id="kpiToImprove" value={answers.kpiToImprove}
                  onChange={(e) => handleAnswerChange('kpiToImprove', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="targetCycleTime">
                  What is the target for cycle time and lead time?
                </label>
                <textarea id="targetCycleTime" value={answers.targetCycleTime}
                  onChange={(e) => handleAnswerChange('targetCycleTime', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="stepsToEliminate">
                  Do you plan to remove or merge any process steps?
                </label>
                <textarea id="stepsToEliminate" value={answers.stepsToEliminate}
                  onChange={(e) => handleAnswerChange('stepsToEliminate', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="wasteToEliminate">
                  Which wastes do you want to eliminate first?
                </label>
                <textarea id="wasteToEliminate" value={answers.wasteToEliminate}
                  onChange={(e) => handleAnswerChange('wasteToEliminate', e.target.value)} rows="3" />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">Lean tools & transformation:</h3>

              <div className="question-item">
                <label htmlFor="leanTools">
                  Which Lean tools do you plan to introduce or strengthen (Kanban, 5S, Heijunka, SMED, etc.)?
                </label>
                <textarea id="leanTools" value={answers.leanTools}
                  onChange={(e) => handleAnswerChange('leanTools', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="plannedChanges">
                  What organizational or technological changes are planned to reach this future state?
                </label>
                <textarea id="plannedChanges" value={answers.plannedChanges}
                  onChange={(e) => handleAnswerChange('plannedChanges', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="plannedTraining">
                  Have you planned training or upskilling actions for your teams?
                </label>
                <textarea id="plannedTraining" value={answers.plannedTraining}
                  onChange={(e) => handleAnswerChange('plannedTraining', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="transformationConstraints">
                  What constraints or resistance have you identified for the transformation?
                </label>
                <textarea id="transformationConstraints" value={answers.transformationConstraints}
                  onChange={(e) => handleAnswerChange('transformationConstraints', e.target.value)} rows="3" />
              </div>
            </div>
          </div>
        );

      /* ----------------- PART 3 (translated) ----------------- */

      case 3:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 3: Gap analysis & transformation monitoring</h2>

            <div className="question-group">
              <h3 className="question-group-title">Gap analysis:</h3>

              <div className="question-item">
                <label htmlFor="measuredGaps">
                  What are the main gaps identified between the current state and the target state?
                </label>
                <textarea id="measuredGaps" value={answers.measuredGaps}
                  onChange={(e) => handleAnswerChange('measuredGaps', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="trackedKPIs">
                  Which KPIs are being monitored to manage the transition?
                </label>
                <textarea id="trackedKPIs" value={answers.trackedKPIs}
                  onChange={(e) => handleAnswerChange('trackedKPIs', e.target.value)} rows="3" />
              </div>

              <div className="question-item">
                <label htmlFor="roadmap">
                  What is your roadmap to reach the future state (milestones, responsibilities, tools)?
                </label>
                <textarea id="roadmap" value={answers.roadmap}
                  onChange={(e) => handleAnswerChange('roadmap', e.target.value)} rows="3" />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  /* ------------------ PDF BUTTON TEXT TRANSLATED ------------------ */

  const handleDownloadPDF = async () => {
    try {
      const element = document.querySelector(".lean-vsm-1");
      if (!element) {
        alert("âŒ Element not found!");
        return;
      }

      document.querySelectorAll("*").forEach((el) => {
        const bgImage = window.getComputedStyle(el).backgroundImage;
        if (bgImage && bgImage.includes("gradient")) {
          el.style.backgroundImage = "none";
        }

        const bgColor = window.getComputedStyle(el).backgroundColor;
        if (
          bgColor.includes("var(") ||
          bgColor === "transparent" ||
          bgColor === "rgba(0, 0, 0, 0)"
        ) {
          el.style.backgroundColor = "#ffffff";
        }
      });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`LeanVSM_${date}.pdf`);
      alert("PDF generated successfully!");
    } catch (err) {
      console.error("PDF error:", err);
      alert("PDF error: " + (err.message || "unknown"));
    }
  };

  const saveLeanAnswers = async (moduleName) => {
    try {
      const caseId = localStorage.getItem("caseId");
      if (!caseId) {
        alert("No active case found!");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lean/${moduleName}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          case_id: caseId,
          answers: answers,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || `Saving error (${response.status}): ${data.message}`
        );
      }

      alert(`âœ… ${moduleName.toUpperCase()} answers saved successfully!`);

      handleNextStep();
    } catch (error) {
      alert("âŒ " + error.message);
    }
  };

  return (
    <div className="container-center-horizontal">
      <div className="lean-vsm-1 screen">
        <div className="header">
          <Link to="/home">
            <img
              className="x1200px-logo_icam_-_2008-1-11"
              src={x1200PxLogo_Icam__20081}
              alt="icam logo"
            />
          </Link>
        </div>

        <div className="questionnaire-container">
          <h1 className="question-title">Value Stream Mapping (VSM)</h1>

          {!showQuestionnaire ? (
            <div className="question-section">
              <p className="question-text">
                Have you already implemented the VSM methodology in your company?
              </p>

              <div className="buttons-container">
                <button
                  className={`choice-button ${hasImplemented ? 'active' : ''}`}
                  onClick={handleYesClick}
                >
                  Yes
                </button>
                <button
                  className={`choice-button ${!hasImplemented ? 'active' : ''}`}
                  onClick={() => setHasImplemented(false)}
                >
                  No
                </button>
              </div>

              {!hasImplemented && (
                <div className="redirect-section">
                  <p className="redirect-text">
                    We recommend starting with the 5S method:
                  </p>
                  <Link to="/5s-1" className="redirect-button">
                    Go to the 5S method
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <>
              {!showImport ? (
                <div className="questionnaire-container-inner" ref={questionnaireRef}>
                  {renderProgressIndicator()}
                  {renderQuestionnaire()}

                  <div className="navigation-buttons">
                    {currentStep > 1 && (
                      <button
                        className="nav-button prev-button"
                        onClick={handlePrevStep}
                      >
                        Previous
                      </button>
                    )}

                    <button
                      className="nav-button next-button"
                      onClick={() => saveLeanAnswers("vsm")}
                    >
                      {currentStep < 3 ? "Next" : "Finish"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="file-upload-section">
                  <h2 className="upload-title">Import your existing documents</h2>
                  <p className="upload-description">
                    You can now import your VSM diagrams, documents or spreadsheets:
                  </p>

                  <div className="upload-container">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden-file-input"
                      accept=".pdf,.jpg,.jpeg,.png,.svg,.xlsx,.xls,.doc,.docx"
                      onChange={handleFileUpload}
                      disabled={uploadStatus.isUploading}
                    />

                    <button
                      className="import-button"
                      onClick={handleImportClick}
                      disabled={uploadStatus.isUploading}
                    >
                      {uploadStatus.isUploading ? "Uploading..." : "Import a file"}
                    </button>

                    {uploadStatus.isUploading && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${uploadStatus.progress}%` }}
                          ></div>
                        </div>
                        <p className="progress-text">
                          Uploading... {uploadStatus.progress}%
                        </p>
                      </div>
                    )}

                    {uploadStatus.error && (
                      <div className="upload-error">
                        <p>{uploadStatus.error}</p>
                      </div>
                    )}

                    {uploadStatus.success && (
                      <div className="upload-success">
                        <p>
                          The file {uploadStatus.fileName} was uploaded successfully!
                        </p>
                        {uploadStatus.fileId && (
                          <button
                            className="download-button"
                            onClick={() =>
                              window.open(`/api/vsm/${uploadStatus.fileId}`, "_blank")
                            }
                          >
                            View file
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="navigation-buttons">
                    <button
                      className="nav-button prev-button"
                      onClick={() => setShowImport(false)}
                    >
                      Back to questionnaire
                    </button>

                    <Link to="/lean-vsm-2" className="nav-button next-button">
                      Continue
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <button
          className="download-button"
          onClick={handleDownloadPDF}
          disabled={uploadStatus.isUploading}
        >
          {uploadStatus.isUploading ? "Generating PDF..." : "Download as PDF"}
        </button>
      </div>
    </div>
  );
}

export default LeanVSM1;
